/**
 * Hook for fetching agent run logs via the backend proxy.
 *
 * Uses the backend /api/sessions/{session_id}/log endpoint which proxies
 * requests to the trace server, avoiding CORS issues.
 *
 * Supports two modes:
 * - Session log: Full run log for a session
 * - Step log: Log for a specific step (function call)
 */

import { useState, useCallback } from 'react';
import { authenticatedFetch } from '../utils/authenticatedFetch';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

export interface TraceLogState {
  data: any | null;
  isLoading: boolean;
  error: string | null;
}

export interface UseTraceLogReturn extends TraceLogState {
  fetchSessionLog: (sessionId: string) => Promise<void>;
  fetchStepLog: (sessionId: string, functionId: string) => Promise<void>;
  clearLog: () => void;
}

export const useTraceLog = (): UseTraceLogReturn => {
  const [state, setState] = useState<TraceLogState>({
    data: null,
    isLoading: false,
    error: null,
  });

  const fetchSessionLog = useCallback(async (sessionId: string) => {
    setState({ data: null, isLoading: true, error: null });

    try {
      const url = `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/log`;
      const response = await authenticatedFetch(url);

      const data = await response.json();

      if (!response.ok) {
        setState({
          data: data || { error: 'log is not available' },
          isLoading: false,
          error: data?.error || `Failed to fetch log: ${response.status}`,
        });
        return;
      }

      setState({ data, isLoading: false, error: null });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch log';
      setState({
        data: { error: 'log is not available' },
        isLoading: false,
        error: errorMsg,
      });
    }
  }, []);

  const fetchStepLog = useCallback(async (sessionId: string, functionId: string) => {
    setState({ data: null, isLoading: true, error: null });

    try {
      const url = `${API_BASE_URL}/api/sessions/${encodeURIComponent(sessionId)}/log?function_id=${encodeURIComponent(functionId)}`;
      const response = await authenticatedFetch(url);

      const data = await response.json();

      if (!response.ok) {
        setState({
          data: data || { error: 'log is not available' },
          isLoading: false,
          error: data?.error || `Failed to fetch step log: ${response.status}`,
        });
        return;
      }

      setState({ data, isLoading: false, error: null });
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to fetch step log';
      setState({
        data: { error: 'log is not available' },
        isLoading: false,
        error: errorMsg,
      });
    }
  }, []);

  const clearLog = useCallback(() => {
    setState({ data: null, isLoading: false, error: null });
  }, []);

  return {
    ...state,
    fetchSessionLog,
    fetchStepLog,
    clearLog,
  };
};
